import { useSyncExternalStore } from 'react';

/**
 * wikiUi — the Wiki's tiny cross-tree UI store.
 *
 * Module-level on purpose: the TopBar and the Wiki nav live in the shell,
 * OUTSIDE the `/wiki/*` route tree, and still need to raise a toast or open
 * the New-page modal that `pages/WikiV2.jsx` renders (via `<WikiToast/>` and
 * `<WikiModalHost/>`). No React provider is required.
 *
 * State shape (read with `useWikiUi(selector)` or `wikiUi.getState()`):
 *   toast      { id: number, message: string } | null
 *   modal      { kind: 'category'|'subcategory'|'page'|'categorySettings'|'movePage'|'confirm',
 *                opts: ModalOpts } | null   (the last three: #872)
 *   askPrefill { question, categoryId, pageId, autoSend } | null
 *   navOpen    { [categoryId]: boolean }   (Wiki nav expanded categories)
 *
 * ModalOpts: { parentId?, categoryId?, subcategoryId?, mode?: 'write'|'import',
 *              template?, category? }  — `mode` defaults to 'write' for 'page'.
 */

export const WIKI_TOAST_MS = 3800;
export const WIKI_MODAL_KINDS = Object.freeze([
    'category', 'subcategory', 'page',
    // #872: Settings (opts.category), Move (opts.page), confirm (see WikiConfirmModal).
    'categorySettings', 'movePage', 'confirm',
]);

/** Verbatim prototype copy used by foundation-owned surfaces. */
export const WIKI_TOASTS = Object.freeze({
    // TopBar "New" for a Member (prototype `topNew`, A §1.4).
    memberCannotCreate: 'Your role can read and ask the Wiki, but not create pages.',
});

const INITIAL = Object.freeze({ toast: null, modal: null, askPrefill: null, navOpen: {} });

let state = INITIAL;
let toastSeq = 0;
let toastTimer = null;
const listeners = new Set();

function emit() {
    listeners.forEach((listener) => listener());
}

function setState(patch) {
    state = { ...state, ...patch };
    emit();
}

function normalizeModalOpts(kind, opts) {
    const next = { ...(opts || {}) };
    if (kind === 'page' && next.mode !== 'import') next.mode = 'write';
    return next;
}

export const wikiUi = {
    getState: () => state,

    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },

    /**
     * Show `message` in the global Wiki toast for 3800 ms. One toast at a
     * time: a new message replaces the current one and restarts the timer
     * (prototype `wkToast`). Returns the toast id.
     */
    toast(message) {
        if (message == null || message === '') return null;
        clearTimeout(toastTimer);
        toastSeq += 1;
        const id = toastSeq;
        setState({ toast: { id, message: String(message) } });
        toastTimer = setTimeout(() => {
            toastTimer = null;
            if (state.toast && state.toast.id === id) setState({ toast: null });
        }, WIKI_TOAST_MS);
        return id;
    },

    dismissToast() {
        clearTimeout(toastTimer);
        toastTimer = null;
        if (state.toast) setState({ toast: null });
    },

    /** Open a create modal rendered by `<WikiModalHost/>`. */
    openModal(kind, opts = {}) {
        if (!WIKI_MODAL_KINDS.includes(kind)) {
            // A typo must not open an empty overlay; say so in dev and stop.
            console.warn(`[wikiUi] unknown modal kind '${kind}'`);
            return;
        }
        setState({ modal: { kind, opts: normalizeModalOpts(kind, opts) } });
    },

    closeModal() {
        if (state.modal) setState({ modal: null });
    },

    /**
     * Hand a question to Ask before navigating there (Home chips, "Ask about
     * this page"). Ask reads it with `peekAskPrefill()` in a lazy initializer
     * and clears it with `consumeAskPrefill()` in an effect.
     */
    setAskPrefill(prefill) {
        if (!prefill) { setState({ askPrefill: null }); return; }
        setState({
            askPrefill: {
                question: prefill.question || '',
                categoryId: prefill.categoryId || null,
                pageId: prefill.pageId || null,
                autoSend: Boolean(prefill.autoSend),
            },
        });
    },

    peekAskPrefill: () => state.askPrefill,

    consumeAskPrefill() {
        const prefill = state.askPrefill;
        if (prefill) setState({ askPrefill: null });
        return prefill;
    },

    /** Wiki nav expansion (persists across Wiki routes and sidebar remounts). */
    setNavOpen(categoryId, open) {
        if (!categoryId) return;
        if (Boolean(state.navOpen[categoryId]) === Boolean(open) && categoryId in state.navOpen) return;
        setState({ navOpen: { ...state.navOpen, [categoryId]: Boolean(open) } });
    },

    toggleNavOpen(categoryId) {
        if (!categoryId) return;
        setState({ navOpen: { ...state.navOpen, [categoryId]: !state.navOpen[categoryId] } });
    },

    /** Test helper: back to the initial state, timers cleared. */
    reset() {
        clearTimeout(toastTimer);
        toastTimer = null;
        state = INITIAL;
        emit();
    },
};

const identity = (value) => value;

/**
 * Subscribe to a slice of the store. The selector must return a value that
 * is stable while the state is unchanged (a state field or a primitive) —
 * never build a new object or array inside it.
 */
export function useWikiUi(selector = identity) {
    const read = () => selector(state);
    return useSyncExternalStore(wikiUi.subscribe, read, read);
}

export default wikiUi;
